import type { Job } from "../../../../../../models";
import { ColumnContext } from "../standard-column-types";
import { FieldNotesColumnViewModel } from "./FieldNotes.column.types";

/**
 * Hook that provides the FieldNotesColumn ViewModel
 * @param context - The column context containing state and helpers
 * @returns ViewModel with all business logic functions
 */
export function useFieldNotesColumnViewModel(context: ColumnContext): FieldNotesColumnViewModel {
	/**
	 * Get current field notes files for job
	 * Uses row.original directly instead of O(n) jobs.find() lookup
	 */
	function getCurrentFiles(job: Job): File[] {
		return job?.files?.fieldNotes || [];
	}

	/**
	 * Remove file at specified index
	 */
	function handleRemoveFile(jobId: string, fileIndex: number): void {
		const newJobs = [...context.jobs];
		const newJob = newJobs.find((j) => j.id === jobId);
		if (!newJob) return;

		// Ensure files object exists
		if (!newJob.files) newJob.files = {};

		const newFiles = [...(newJob.files.fieldNotes || [])];
		newFiles.splice(fileIndex, 1);
		newJob.files.fieldNotes = newFiles;

		context.setJobs(newJobs);
		context.validateJobs([newJob], { updateState: true, clearPrevious: false });
	}

	/**
	 * Add new files from file input
	 */
	function handleAddFiles(jobId: string, files: FileList): void {
		const newJobs = [...context.jobs];
		const newJob = newJobs.find((j) => j.id === jobId);
		if (!newJob) return;

		// Ensure files object exists
		if (!newJob.files) newJob.files = {};

		const existingFiles = newJob.files.fieldNotes || [];
		const newFileArray = Array.from(files);
		newJob.files.fieldNotes = [...existingFiles, ...newFileArray];

		context.setJobs(newJobs);
		context.validateJobs([newJob], { updateState: true, clearPrevious: false });
	}

	return {
		getCurrentFiles,
		handleRemoveFile,
		handleAddFiles,
	};
}
